import { Alert, Badge, Button, cardClass, fieldClass, selectClass, Table, textareaClass } from '@ledgerloom/ui';
import { useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, propose, type Outcome } from '../api';
import { useApi, usePageTitle } from '../hooks';
import { useSession } from '../session';
import { Breadcrumb, Empty, ErrorBanner, Expandable, ListSkeleton, Loading, outcomeMessage, outcomeTone, schemaLabel, Time, ToggleGroup, useToast } from '../components/ui';
import { CardBoard, changedCards, type KanbanCard } from '../components/CardBoard';
import { Field } from '../components/Field';
import { day } from '../format';

// Tickets: work tracked to resolution. The list, with saved views and a board,
// and one ticket's conversation, properties, linked records, time, and timeline.
// A ticket is a record, not an Inbox item: its decisions reach the Inbox and link
// back here. Every change is a proposal. Patterns: data-display/table,
// data-display/kanban-board, data-display/timeline, navigation/load-more,
// forms/text-field, forms/selection-input.

export interface TicketState { id: string; label: string; class: 'open' | 'pending' | 'resolved' | 'closed' }
export interface TicketMove { from: string; to: string; who: string[] }
export interface TicketType { id: string; key: string; name: string; states: TicketState[]; transitions: TicketMove[]; fields: string[]; sla_policy: string | null; default_queue: string | null; version: number }
export interface TicketQueue { id: string; key: string; name: string; mailbox_id: string | null; mailbox_name: string | null; members: string[]; default_assignee: string | null; version: number }
export interface SlaPolicy { id: string; key: string; name: string; first_response_minutes: Record<string, number>; resolution_minutes: Record<string, number>; calendar: 'business_hours' | '24x7'; pauses_in: string[]; version: number }
export interface TicketView { id: string; name: string; filter: Record<string, unknown>; columns: string[]; board: 'state' | 'assignee' | 'priority'; shared: boolean; version: number }
export interface TicketSettingsData { types: TicketType[]; queues: TicketQueue[]; sla_policies: SlaPolicy[]; views: TicketView[] }

interface Ticket {
  id: string; number: number; subject: string; state: string; state_label: string; state_class: TicketState['class']; priority: Priority; assignee: string | null; assignee_name: string | null;
  party_id: string | null; party_name: string | null; requester_email: string | null; type: string; type_name: string; queue: string; queue_name: string;
  due_first_response_at: string | null; due_resolution_at: string | null; first_responded_at: string | null; resolved_at: string | null; paused: boolean;
  first_response_breached_at: string | null; resolution_breached_at: string | null; status: 'active' | 'merged'; merged_into_id: string | null; version: number; created_at: string;
}
interface TicketDetailData extends Ticket {
  links: Array<{ kind: string; id: string; title: string; visible: boolean }>;
  merged: Array<{ id: string; number: number }>;
  messages: Array<{ id: string; direction: 'in' | 'out' | 'internal'; from_actor: string; from_name: string | null; body: string; created_at: string }>;
  time: Array<{ id: string; actor_name: string | null; minutes: number; on_date: string; note: string | null; reverses_id: string | null; reversed: boolean }>;
  pending: Array<{ id: string; schema_id: string; state: string; rationale: string | null }>;
  timeline: Array<{ seq: string; op: string; change: any; at: string; by: string | null }>;
}
type Priority = 'low' | 'normal' | 'high' | 'urgent';
const PRIORITIES: Array<[Priority, string]> = [['low', 'Low'], ['normal', 'Normal'], ['high', 'High'], ['urgent', 'Urgent']];
const priorityWord = (p: string) => PRIORITIES.find(([k]) => k === p)?.[1] ?? p;
const CLASS_TONE: Record<TicketState['class'], 'neutral' | 'info' | 'warning' | 'success'> = { open: 'info', pending: 'warning', resolved: 'success', closed: 'neutral' };
const requesterOf = (t: Ticket) => t.party_name ?? t.requester_email ?? '–';
const breached = (t: Ticket) => !!t.first_response_breached_at || !!t.resolution_breached_at;
const nextDue = (t: Ticket) => (t.resolved_at || t.paused ? null : (!t.first_responded_at && t.due_first_response_at) || t.due_resolution_at);
const LINK_WORD: Record<string, string> = { party: 'Contact', deal: 'Deal', lead: 'Lead', quote: 'Quote', activity: 'Activity', ticket: 'Ticket' };
const LINK_PATH: Record<string, string> = { party: '/contacts', deal: '/deals', quote: '/quotes', ticket: '/tickets' };
const PAGE = 100;

function useSend(done: () => unknown) {
  const toast = useToast();
  const { refreshStats } = useSession();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const send = async (schema: string, payload: unknown, saved: string): Promise<Outcome | null> => {
    setBusy(true); setError(null);
    try {
      const out = await propose(schema, payload);
      toast(outcomeMessage(out, saved), outcomeTone(out));
      // A refusal may mean the ticket moved on: read it again either way.
      await done();
      void refreshStats();
      return out;
    } catch (e) { setError(e); return null; } finally { setBusy(false); }
  };
  return { busy, error, send };
}

// Finds a contact by name or address, for a requester or a link.
function ContactSearch({ label, onPick }: { label: string; onPick: (c: { id: string; display_name: string }) => void }) {
  const [q, setQ] = useState('');
  const found = useApi<{ items: Array<{ id: string; display_name: string; email: string | null }> }>(q.trim().length >= 2 ? `/v1/parties?q=${encodeURIComponent(q.trim())}&limit=5` : null, [q]);
  return (
    <div className="stack" style={{ gap: 6 }}>
      <Field label={label}><input className={fieldClass} value={q} onChange={(e) => setQ(e.target.value)} /></Field>
      {q.trim().length >= 2 && found.data && (found.data.items.length
        ? <ul className="row" style={{ gap: 6, flexWrap: 'wrap', listStyle: 'none', padding: 0, margin: 0 }} aria-label={`Contacts matching ${q.trim()}`}>
          {found.data.items.map((c) => <li key={c.id}><Button type="button" variant="ghost" size="sm" onClick={() => { onPick(c); setQ(''); }}>{c.display_name}{c.email ? ` (${c.email})` : ''}</Button></li>)}
        </ul>
        : <p className="small muted">No contact matches.</p>)}
    </div>
  );
}

function NewTicket({ types, onClose }: { types: TicketType[]; onClose: () => void }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({ type: types[0]!.key, subject: '', requester: '', priority: 'normal' as Priority, body: '' });
  const [party, setParty] = useState<{ id: string; display_name: string } | null>(null);
  const { busy, error, send } = useSend(() => undefined);
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!form.subject.trim()) return;
    const out = await send('ticket.create', {
      type: form.type, subject: form.subject.trim(), priority: form.priority,
      ...(party ? { party_id: party.id } : form.requester.trim() ? { requester_email: form.requester.trim() } : {}),
      ...(form.body.trim() ? { body: form.body.trim() } : {}),
    }, 'Ticket opened');
    if (out?.state === 'written') navigate(`/tickets/${out.result!.ticket_id}`);
  }
  return (
    <form className={`${cardClass} card-pad stack`} onSubmit={(e) => void submit(e)} aria-label="New ticket">
      <ErrorBanner error={error} />
      <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
        {types.length > 1 && <Field label="Type"><select className={selectClass} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>{types.map((t) => <option key={t.key} value={t.key}>{t.name}</option>)}</select></Field>}
        <Field label="Priority"><select className={selectClass} value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value as Priority })}>{PRIORITIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></Field>
      </div>
      <Field label="Subject" required><input className={fieldClass} value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} /></Field>
      {party
        ? <p>From <strong>{party.display_name}</strong> <Button type="button" variant="ghost" size="sm" onClick={() => setParty(null)}>Change</Button></p>
        : <div className="grid-2"><ContactSearch label="Requester, if a contact" onPick={setParty} /><Field label="Or their address"><input className={fieldClass} type="email" value={form.requester} onChange={(e) => setForm({ ...form, requester: e.target.value })} /></Field></div>}
      <Field label="What it is about"><textarea className={textareaClass} rows={3} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} /></Field>
      <div className="row" style={{ gap: 8 }}>
        <Button type="submit" disabled={busy || !form.subject.trim()}>Open ticket</Button>
        <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
      </div>
    </form>
  );
}

const CELLS: Record<string, { head: string; cell: (t: Ticket) => ReactNode }> = {
  number: { head: 'Number', cell: (t) => `#${t.number}` },
  subject: { head: 'Subject', cell: (t) => <Link to={`/tickets/${t.id}`}>{t.subject}</Link> },
  state: { head: 'State', cell: (t) => <Badge tone={CLASS_TONE[t.state_class]}>{t.state_label}</Badge> },
  priority: { head: 'Priority', cell: (t) => priorityWord(t.priority) },
  assignee: { head: 'Assignee', cell: (t) => t.assignee_name ?? 'Nobody' },
  requester: { head: 'Requester', cell: requesterOf },
  queue: { head: 'Queue', cell: (t) => t.queue_name },
  type: { head: 'Type', cell: (t) => t.type_name },
  due: { head: 'Due', cell: (t) => (breached(t) ? <Badge tone="danger">Past due</Badge> : nextDue(t) ? <Time iso={nextDue(t)!} full /> : '–') },
  created: { head: 'Opened', cell: (t) => <Time iso={t.created_at} /> },
};
const DEFAULT_COLUMNS = ['number', 'subject', 'state', 'priority', 'assignee', 'requester', 'due'];

function TicketBoard({ tickets, type, by, onChanged }: { tickets: Ticket[]; type: TicketType; by: TicketView['board']; onChanged: () => unknown }) {
  const { me } = useSession();
  const toast = useToast();
  const users = useApi<Array<{ id: string; display_name: string; status: string }>>('/v1/users');
  const [local, setLocal] = useState<KanbanCard[] | null>(null);
  const [busyIds, setBusyIds] = useState<ReadonlySet<string>>(new Set());
  const byId = useMemo(() => new Map(tickets.map((t) => [t.id, t])), [tickets]);
  const fromServer = useMemo(() => tickets.map((t): KanbanCard => ({
    id: t.id, title: `#${t.number} ${t.subject}`, status: by === 'priority' ? t.priority : t.state, assignee: t.assignee,
    subtitle: requesterOf(t), meta: priorityWord(t.priority), flags: breached(t) ? ['Past due'] : [], href: `/tickets/${t.id}`,
  })), [tickets, by]);
  const people = useMemo(() => {
    const list = (users.data ?? []).filter((u) => u.status === 'active').map((u) => ({ id: `user:${u.id}`, name: u.display_name }));
    for (const t of tickets) if (t.assignee && !list.some((p) => p.id === t.assignee)) list.push({ id: t.assignee, name: t.assignee_name ?? 'Someone' });
    return list;
  }, [users.data, tickets]);
  const cards = (local ?? fromServer).map((c) => (busyIds.has(c.id) ? { ...c, busy: true } : c));
  async function apply(next: KanbanCard[]) {
    const changes = changedCards(local ?? fromServer, next);
    setLocal(next);
    setBusyIds((b) => new Set([...b, ...changes.map((c) => c.card.id)]));
    try {
      for (const { card, was } of changes) {
        const t = byId.get(card.id);
        if (!was || !t) continue;
        let version = t.version;
        if (card.status !== was.status) {
          const out = await propose('ticket.update', { ticket_id: t.id, base_version: version, ...(by === 'priority' ? { set: { priority: card.status } } : { state: card.status }) });
          toast(outcomeMessage(out, `Moved #${t.number}`), outcomeTone(out));
          if (out.state === 'written') version += 1;
        }
        if (card.assignee !== was.assignee) {
          const out = await propose('ticket.assign', { ticket_id: t.id, base_version: version, assignee: card.assignee });
          toast(outcomeMessage(out, `Assigned #${t.number}`), outcomeTone(out));
        }
      }
    } catch (e) { toast((e as Error).message, 'error'); } finally {
      await onChanged();
      setLocal(null);
      setBusyIds(new Set());
    }
  }
  return (
    <CardBoard title={`${type.name} tickets`} names={{ status: by === 'priority' ? 'Priority' : 'State', assignee: 'Assignee' }} fixed={['add', 'title', 'description']} defaultGroupBy={by === 'assignee' ? 'assignee' : 'status'}
      columns={by === 'priority' ? PRIORITIES.map(([id, title]) => ({ id, title })) : type.states.map((s) => ({ id: s.id, title: s.label }))}
      people={people} currentUser={`user:${me.user_id}`} cards={cards} onCardsChange={(next) => void apply(next)} />
  );
}

function SaveView({ filter, onSaved }: { filter: Record<string, unknown>; onSaved: () => unknown }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [shared, setShared] = useState(false);
  const { busy, error, send } = useSend(onSaved);
  if (!open) return <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(true)}>Save as a view</Button>;
  return (
    <form className="row" style={{ gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }} aria-label="Save as a view"
      onSubmit={(e) => { e.preventDefault(); void send('config.ticket_view', { name: name.trim(), filter, columns: DEFAULT_COLUMNS, shared, reason: 'Saved from Tickets' }, 'View saved').then((o) => { if (o && o.state !== 'rejected') { setOpen(false); setName(''); } }); }}>
      <ErrorBanner error={error} />
      <Field label="Name of the view"><input className={fieldClass} value={name} onChange={(e) => setName(e.target.value)} /></Field>
      <label className="row small" style={{ gap: 6 }}><input type="checkbox" checked={shared} onChange={(e) => setShared(e.target.checked)} /> Share with everyone</label>
      <Button type="submit" variant="secondary" size="sm" disabled={busy || !name.trim()}>Save</Button>
      <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
    </form>
  );
}

export function Tickets() {
  usePageTitle('Tickets');
  const settings = useApi<TicketSettingsData>('/v1/tickets/settings');
  const [show, setShow] = useState<'list' | 'board'>('list');
  const [scope, setScope] = useState<'open' | 'all'>('open');
  const [viewId, setViewId] = useState('');
  const [queue, setQueue] = useState('');
  const [type, setType] = useState('');
  const [q, setQ] = useState('');
  const [creating, setCreating] = useState(false);
  const s = settings.data;
  const view = s?.views.find((v) => v.id === viewId) ?? null;
  const boardType = s?.types.find((t) => t.key === type) ?? s?.types[0] ?? null;
  const asBoard = show === 'board';
  const params = new URLSearchParams();
  if (viewId) params.set('view', viewId);
  if (queue) params.set('queue', queue);
  if (asBoard ? boardType : type) params.set('type', asBoard ? boardType!.key : type);
  if (q.trim()) params.set('q', q.trim());
  if (scope === 'open' && !asBoard) params.set('open', 'true');
  // shortcut: a board shows the newest 500 of a type, the most a page holds; load more by column when a workspace has more.
  params.set('limit', String(asBoard ? 500 : PAGE));
  const qs = params.toString();
  const list = useApi<{ items: Ticket[]; total: number }>(s ? `/v1/tickets?${qs}` : null, [qs, !!s]);
  const [extra, setExtra] = useState<{ qs: string; rows: Ticket[] } | null>(null);
  const [moreBusy, setMoreBusy] = useState(false);
  const [moreError, setMoreError] = useState<unknown>(null);
  const rows = list.data ? [...list.data.items, ...(extra?.qs === qs ? extra.rows : [])] : null;
  const hasMore = !asBoard && !!list.data && rows!.length < list.data.total;
  async function more() {
    setMoreBusy(true); setMoreError(null);
    try {
      const page = await api<{ items: Ticket[] }>(`/v1/tickets?${qs}&offset=${rows!.length}`);
      const seen = new Set(rows!.map((t) => t.id));
      setExtra({ qs, rows: [...(extra?.qs === qs ? extra.rows : []), ...page.items.filter((t) => !seen.has(t.id))] });
    } catch (e) { setMoreError(e); } finally { setMoreBusy(false); }
  }
  const columns = (view?.columns ?? DEFAULT_COLUMNS).filter((c) => CELLS[c]);
  const narrowed = [queue ? { '==': [{ var: 'queue' }, queue] } : null, type ? { '==': [{ var: 'type' }, type] } : null].filter(Boolean);
  return (
    <div className="stack">
      <div className="page-head">
        <div><h1>Tickets</h1><p>Work tracked to resolution, with its conversation and its clock.</p></div>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <ToggleGroup label="Show as" value={show} options={[['list', 'List'], ['board', 'Board']]} onChange={setShow} />
          {s && s.types.length > 0 && <Button type="button" onClick={() => setCreating(true)}>New ticket</Button>}
        </div>
      </div>
      <ErrorBanner error={settings.error ?? list.error} />
      {creating && s && <NewTicket types={s.types} onClose={() => setCreating(false)} />}
      <div className="row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        {!!s?.views.length && <Field label="View"><select className={selectClass} value={viewId} onChange={(e) => setViewId(e.target.value)}><option value="">All tickets</option>{s.views.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}</select></Field>}
        {(s?.queues.length ?? 0) > 1 && <Field label="Queue"><select className={selectClass} value={queue} onChange={(e) => setQueue(e.target.value)}><option value="">Every queue</option>{s!.queues.map((x) => <option key={x.key} value={x.key}>{x.name}</option>)}</select></Field>}
        {(s?.types.length ?? 0) > 1 && <Field label="Type"><select className={selectClass} value={asBoard ? boardType?.key ?? '' : type} onChange={(e) => setType(e.target.value)}>{!asBoard && <option value="">Every type</option>}{s!.types.map((x) => <option key={x.key} value={x.key}>{x.name}</option>)}</select></Field>}
        <Field label="Find"><input className={fieldClass} type="search" value={q} onChange={(e) => setQ(e.target.value)} /></Field>
        {!asBoard && <ToggleGroup label="Which tickets" value={scope} options={[['open', 'Not resolved'], ['all', 'All']]} onChange={setScope} />}
        {narrowed.length > 0 && !viewId && <SaveView filter={narrowed.length === 1 ? narrowed[0]! : { and: narrowed }} onSaved={() => settings.reload()} />}
      </div>
      {list.loading && !rows ? <section className={cardClass}><ListSkeleton rows={5} label="Loading tickets" /></section>
        : !rows?.length ? <section className={cardClass}><Empty title="No tickets here" /></section>
        : asBoard && boardType ? <TicketBoard tickets={rows} type={boardType} by={view?.board ?? 'state'} onChanged={() => list.reload()} />
        : (
          <section className={cardClass}>
            <Table label="Tickets" id="ticket-list">
              <caption className="sr-only">Tickets</caption>
              <thead><tr>{columns.map((c) => <th key={c} scope="col">{CELLS[c]!.head}</th>)}</tr></thead>
              <tbody>{rows.map((t) => <tr key={t.id}>{columns.map((c) => <td key={c}>{CELLS[c]!.cell(t)}</td>)}</tr>)}</tbody>
            </Table>
          </section>
        )}
      <ErrorBanner error={moreError} />
      {hasMore && <div><Button type="button" variant="secondary" onClick={() => void more()} disabled={moreBusy} aria-controls="ticket-list">{moreBusy ? 'Loading…' : 'Load more tickets'}</Button></div>}
    </div>
  );
}

// What happened to a ticket, from its events, in plain words.
function timelineWords(e: TicketDetailData['timeline'][number], labelOf: (id: string) => string): string {
  switch (e.op) {
    case 'create': return 'Opened';
    case 'state': return e.change?.state ? `Moved from ${labelOf(e.change.state.from)} to ${labelOf(e.change.state.to)}${e.change.reason ? `: ${e.change.reason}` : ''}` : 'Moved';
    case 'assign': return e.change?.to ? 'Assigned' : 'Unassigned';
    case 'update': return 'Changed';
    case 'link': return 'Linked records changed';
    case 'replied': return 'Reply sent';
    case 'sla_breached': return e.change?.clock === 'first_response' ? 'Missed its first response time' : 'Missed its resolution time';
    case 'merged_into': return 'Merged into another ticket';
    case 'merge_survivor': return `Ticket #${e.change?.merged_number} merged in`;
    default: return 'Changed';
  }
}

const DIRECTION_WORD = { in: 'From the requester', out: 'Reply sent', internal: 'Internal note' } as const;

export function TicketDetail() {
  const { id = '' } = useParams();
  const ticket = useApi<TicketDetailData>(`/v1/tickets/${id}`, [id]);
  const settings = useApi<TicketSettingsData>('/v1/tickets/settings');
  const users = useApi<Array<{ id: string; display_name: string; status: string }>>('/v1/users');
  const t = ticket.data;
  usePageTitle(t ? `#${t.number} ${t.subject}` : 'Ticket');
  const reload = () => ticket.reload();
  const { busy, error, send } = useSend(reload);
  const [reply, setReply] = useState('');
  const [note, setNote] = useState('');
  const [minutes, setMinutes] = useState('');
  const [timeNote, setTimeNote] = useState('');
  const [dup, setDup] = useState('');
  // shortcut: a person picks a duplicate among the newest page of open tickets; add a search here when older duplicates are missed (the API takes any).
  const others = useApi<{ items: Ticket[] }>(t?.status === 'active' ? '/v1/tickets?limit=100&open=true' : null, [t?.status]);
  const type = settings.data?.types.find((x) => x.key === t?.type) ?? null;
  const labelOf = (s: string) => type?.states.find((x) => x.id === s)?.label ?? s;
  if (!t) return <div className="stack"><ErrorBanner error={ticket.error} />{ticket.loading && <Loading label="Loading ticket" />}</div>;
  const moves = (type?.transitions ?? []).filter((m) => m.from === t.state && m.who.includes('person'));
  const canReply = t.status === 'active' && (!!t.party_id || !!t.requester_email);
  const open = t.status === 'active';
  const total = t.time.reduce((n, e) => n + e.minutes, 0);
  const queue = settings.data?.queues.find((q) => q.key === t.queue);
  const assignees = (queue?.members.length ? queue.members : (users.data ?? []).filter((u) => u.status === 'active').map((u) => `user:${u.id}`));
  const nameOf = (a: string) => (a === t.assignee && t.assignee_name) || users.data?.find((u) => `user:${u.id}` === a)?.display_name || 'Someone';
  return (
    <div className="stack">
      <Breadcrumb items={[{ to: '/tickets', label: 'Tickets' }, { label: `#${t.number}` }]} />
      <div className="page-head">
        <div><h1>{t.subject}</h1><p>#{t.number} · {t.type_name} · {t.queue_name}</p></div>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <Badge tone={CLASS_TONE[t.state_class]}>{t.state_label}</Badge>
          {breached(t) && <Badge tone="danger">Past due</Badge>}
          {t.paused && !t.resolved_at && <Badge tone="neutral">Clock paused</Badge>}
        </div>
      </div>
      {t.status === 'merged' && t.merged_into_id && <Alert role="note" tone="info">This ticket was merged. <Link to={`/tickets/${t.merged_into_id}`}>Open the ticket it merged into</Link>.</Alert>}
      <ErrorBanner error={error ?? ticket.error} />
      {!!t.pending.length && (
        <Alert role="note" tone="info">Waiting for a decision: {t.pending.map((p, i) => <span key={p.id}>{i > 0 && ', '}<Link to={`/activity/${p.id}`}>{schemaLabel(p.schema_id)}</Link></span>)}</Alert>
      )}
      <div className="grid-main">
        <div className="stack">
          <section className={`${cardClass} card-pad stack`} aria-label="Conversation">
            <h2>Conversation</h2>
            {t.merged.length > 0 && <p className="small muted">With what was on {t.merged.map((m) => `#${m.number}`).join(', ')}, merged into this ticket.</p>}
            {t.messages.length ? (
              <ol className="stack" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {t.messages.map((m) => (
                  <li key={m.id} className="stack" style={{ gap: 4 }} aria-label={DIRECTION_WORD[m.direction]}>
                    <div className="small muted"><strong>{DIRECTION_WORD[m.direction]}</strong> · {m.from_name ?? m.from_actor} · <Time iso={m.created_at} /></div>
                    <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{m.body}</div>
                  </li>
                ))}
              </ol>
            ) : <p className="muted">Nothing written yet.</p>}
          </section>
          {canReply && (
            <form className={`${cardClass} card-pad stack`} aria-label="Reply to the requester"
              onSubmit={(e) => { e.preventDefault(); if (reply.trim()) void send('ticket.message.reply', { ticket_id: t.id, body_text: reply.trim() }, 'Reply sent').then((o) => { if (o && o.state !== 'rejected') setReply(''); }); }}>
              <Field label={`Reply to ${requesterOf(t)}`}><textarea className={textareaClass} rows={4} value={reply} onChange={(e) => setReply(e.target.value)} /></Field>
              <div><Button type="submit" disabled={busy || !reply.trim()}>Send reply</Button></div>
            </form>
          )}
          {open && (
            <form className={`${cardClass} card-pad stack`} aria-label="Add an internal note"
              onSubmit={(e) => { e.preventDefault(); if (note.trim()) void send('ticket.message.internal', { ticket_id: t.id, body: note.trim() }, 'Note added').then((o) => { if (o && o.state !== 'rejected') setNote(''); }); }}>
              <Field label="Internal note" hint="The requester never sees it."><textarea className={textareaClass} rows={2} value={note} onChange={(e) => setNote(e.target.value)} /></Field>
              <div><Button type="submit" variant="secondary" disabled={busy || !note.trim()}>Add note</Button></div>
            </form>
          )}
          <section className={`${cardClass} card-pad stack`} aria-label="Timeline">
            <h2>Timeline</h2>
            <ol className="stack small" style={{ listStyle: 'none', padding: 0, margin: 0, gap: 6 }}>
              {t.timeline.map((e) => <li key={e.seq}>{timelineWords(e, labelOf)}{e.by && !e.by.startsWith('system:') ? ` by ${e.by}` : ''} · <Time iso={e.at} /></li>)}
            </ol>
          </section>
        </div>
        <div className="stack">
          <section className={`${cardClass} card-pad stack`} aria-label="Properties">
            <h2>Properties</h2>
            <dl className="stack small" style={{ gap: 4, margin: 0 }}>
              <div><dt className="muted">Requester</dt><dd style={{ margin: 0 }}>{t.party_id ? <Link to={`/contacts/${t.party_id}`}>{t.party_name}</Link> : t.requester_email ?? 'Nobody'}</dd></div>
              <div><dt className="muted">Opened</dt><dd style={{ margin: 0 }}><Time iso={t.created_at} full /></dd></div>
              {t.due_first_response_at && <div><dt className="muted">First response</dt><dd style={{ margin: 0 }}>{t.first_responded_at ? <>Answered <Time iso={t.first_responded_at} /></> : <>Due <Time iso={t.due_first_response_at} full /></>}</dd></div>}
              {t.due_resolution_at && <div><dt className="muted">Resolution</dt><dd style={{ margin: 0 }}>{t.resolved_at ? <>Resolved <Time iso={t.resolved_at} /></> : <>Due <Time iso={t.due_resolution_at} full /></>}</dd></div>}
            </dl>
            {open && moves.length > 0 && (
              <div className="row" style={{ gap: 6, flexWrap: 'wrap' }} role="group" aria-label="Move this ticket">
                {moves.map((m) => <Button key={m.to} type="button" variant="secondary" size="sm" disabled={busy} onClick={() => void send('ticket.update', { ticket_id: t.id, base_version: t.version, state: m.to }, `Moved to ${labelOf(m.to)}`)}>Move to {labelOf(m.to)}</Button>)}
              </div>
            )}
            {open && <>
              <Field label="Priority"><select className={selectClass} value={t.priority} disabled={busy} onChange={(e) => void send('ticket.update', { ticket_id: t.id, base_version: t.version, set: { priority: e.target.value } }, 'Priority changed')}>{PRIORITIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></Field>
              <Field label="Assignee">
                <select className={selectClass} value={t.assignee ?? ''} disabled={busy} onChange={(e) => void send('ticket.assign', { ticket_id: t.id, base_version: t.version, assignee: e.target.value || null }, 'Assigned')}>
                  <option value="">Nobody</option>
                  {[...new Set([...(t.assignee ? [t.assignee] : []), ...assignees])].map((a) => <option key={a} value={a}>{nameOf(a)}</option>)}
                </select>
              </Field>
            </>}
          </section>
          {(t.links.length > 0 || open) && (
            <section className={`${cardClass} card-pad stack`} aria-label="Linked records">
              <h2>Linked records</h2>
              {t.links.map((l) => (
                <div key={`${l.kind}:${l.id}`} className="row small" style={{ gap: 6, justifyContent: 'space-between' }}>
                  <span>{LINK_WORD[l.kind]}: {l.visible && LINK_PATH[l.kind] ? <Link to={`${LINK_PATH[l.kind]}/${l.id}`}>{l.title}</Link> : l.title}</span>
                  {open && <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={() => void send('ticket.link', { ticket_id: t.id, remove: [{ kind: l.kind, id: l.id }] }, 'Link taken off')}>Take off</Button>}
                </div>
              ))}
              {open && <ContactSearch label="Link a contact" onPick={(c) => void send('ticket.link', { ticket_id: t.id, add: [{ kind: 'party', id: c.id }] }, `Linked ${c.display_name}`)} />}
            </section>
          )}
          <section className={`${cardClass} card-pad stack`} aria-label="Time">
            <h2>Time</h2>
            {t.time.length > 0 && <>
              <p className="small">{total} minute{total === 1 ? '' : 's'} in all</p>
              <ul className="stack small" style={{ listStyle: 'none', padding: 0, margin: 0, gap: 4 }}>
                {t.time.map((e) => (
                  <li key={e.id} className="row" style={{ gap: 6, justifyContent: 'space-between' }}>
                    <span>{e.minutes} min · {e.actor_name} · {day(e.on_date)}{e.note ? ` · ${e.note}` : ''}{e.reverses_id ? ' · reversal' : ''}</span>
                    {open && !e.reverses_id && !e.reversed && <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={() => void send('ticket.time.log', { ticket_id: t.id, reverses_id: e.id }, 'Reversed')}>Reverse</Button>}
                  </li>
                ))}
              </ul>
            </>}
            {open && (
              <form className="row" style={{ gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }} aria-label="Log time"
                onSubmit={(e) => { e.preventDefault(); if (Number(minutes) > 0) void send('ticket.time.log', { ticket_id: t.id, minutes: Math.floor(Number(minutes)), ...(timeNote.trim() ? { note: timeNote.trim() } : {}) }, 'Time logged').then((o) => { if (o && o.state !== 'rejected') { setMinutes(''); setTimeNote(''); } }); }}>
                <Field label="Minutes"><input className={fieldClass} type="number" min={1} step={1} value={minutes} onChange={(e) => setMinutes(e.target.value)} style={{ width: 96 }} /></Field>
                <Field label="What it was for" className="grow"><input className={fieldClass} value={timeNote} onChange={(e) => setTimeNote(e.target.value)} /></Field>
                <Button type="submit" variant="secondary" size="sm" disabled={busy || !(Number(minutes) > 0)}>Log time</Button>
              </form>
            )}
          </section>
          {open && !!others.data?.items.some((o) => o.id !== t.id) && (
            <section className={`${cardClass} card-pad`} aria-label="Merge a duplicate">
              <Expandable label={['Merge a duplicate into this ticket', 'Hide merging']}>
                <form className="stack" onSubmit={(e) => {
                  e.preventDefault();
                  const other = others.data!.items.find((o) => o.id === dup);
                  if (other) void send('ticket.merge', { ticket_id: t.id, merged_ticket_id: other.id, merged_base_version: other.version }, `Merged #${other.number}`).then(() => { setDup(''); void others.reload(); });
                }}>
                  <Field label="The duplicate" hint="It stays readable from this ticket, with the status merged. Nothing is deleted."><select className={selectClass} value={dup} onChange={(e) => setDup(e.target.value)}><option value="">Choose a ticket</option>{others.data!.items.filter((o) => o.id !== t.id).map((o) => <option key={o.id} value={o.id}>#{o.number} {o.subject}</option>)}</select></Field>
                  <div><Button type="submit" variant="secondary" size="sm" disabled={busy || !dup}>Merge it here</Button></div>
                </form>
              </Expandable>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
